<template>
  <Dialog
    v-model="useDialogStore().workspaceTaxSettings.isOpen"
    size="small-hug"
    @open="onOpen"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('workspace.taxSettings')"
        :show-close="true"
        @close="close"
      />
    </template>

    <div
      v-if="isLoading"
      v-loading="true"
      class="h-40 w-full"
    />
    <div
      v-else
      class="flex flex-col gap-4 w-full"
    >
      <div class="text-sm text-gray-500">
        {{ useT('workspace.taxSettings.paysIn', { currency: currency.toUpperCase() }) }}
      </div>

      <Form
        v-if="requiredFields.length"
        ref="formRef"
        :model="form"
        :rules="rules"
        @submit="onSubmit"
      >
        <InputBlock
          v-if="requiredFields.includes('taxNumber')"
          :label="useT('bcd309')"
          prop="taxNumber"
        >
          <TextInput
            v-model="form.taxNumber"
            name="taxNumber"
            placeholder="24721123"
          />
          <div class="text-xs text-gray-500 mt-1 w-full">
            {{ useT('workspace.taxSettings.fieldHint') }}
          </div>
        </InputBlock>

        <InputBlock
          v-if="requiredFields.includes('vatId')"
          :label="useT('workspace.vatId')"
          prop="vatId"
        >
          <TextInput
            v-model="form.vatId"
            name="vatId"
            placeholder="DE123456789"
          />
          <div class="text-xs text-gray-500 mt-1 w-full">
            {{ useT('workspace.taxSettings.fieldHint') }}
          </div>
        </InputBlock>
      </Form>

      <!-- A currency that does not require a VAT ID leaves it to the customer. -->
      <div v-if="!requiredFields.includes('vatId')">
        <div class="text-sm font-medium text-gray-700">
          {{ useT('workspace.vatId') }}
        </div>
        <div class="text-sm mt-1">
          {{ customerVatId || useT('workspace.taxSettings.notSet') }}
        </div>
        <div class="text-xs text-gray-500 mt-1">
          {{ useT('workspace.taxSettings.customerVatIdHint') }}
        </div>
      </div>
    </div>

    <template
      v-if="!isLoading && requiredFields.length"
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="useT('bcd15')"
        :text-cancel="useT('bcd205')"
        align="right"
        :loading="isSubmitting"
        @submit="formRef.onSubmit()"
        @cancel="useDialogStore().close('workspaceTaxSettings')"
      />
    </template>
  </Dialog>
</template>

<script setup>
// Refs
const form = ref({
  taxNumber: '',
  vatId: '',
});

// The workspace the form was loaded for; a save goes there and nowhere else.
const loadedDomain = ref(null);
const currency = ref('');
// Interface answers what support corrects in this workspace's currency, from its rules.
const requiredFields = ref([]);
const customerVatId = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const formRef = ref(null);

// Computed
// Required from the creation on, so each can be corrected but not removed.
const rules = computed(() => Object.fromEntries(requiredFields.value.map((field) => [field, [useFormRules().required]])));

const startRequest = createLatestOnly();
// Whether the dialog is still the opening a read or a save started in.
let isCurrentOpening = () => false;

// Methods
const onOpen = async() => {
  isLoading.value = true;
  isSubmitting.value = false;
  loadedDomain.value = null;

  const domain = useDialogStore().workspaceTaxSettings.workspaceDomain;
  const isLatest = startRequest();
  isCurrentOpening = isLatest;
  const response = await useWorkspacesStore().getTaxSettings(domain);

  // Reopened for another workspace meanwhile: that opening owns the form now.
  if (!isLatest()) {
    return;
  }

  if (!response) {
    useDialogStore().close('workspaceTaxSettings');
    return;
  }

  loadedDomain.value = domain;
  currency.value = response.currency;
  requiredFields.value = response.requiredWorkspaceFields;
  customerVatId.value = response.vatId;
  form.value = {
    taxNumber: response.taxNumber || '',
    vatId: response.vatId || '',
  };

  isLoading.value = false;
};

const onSubmit = async() => {
  const isSameOpening = isCurrentOpening;
  isSubmitting.value = true;

  const response = await useWorkspacesStore().updateTaxSettings(loadedDomain.value, form.value);

  if (response) {
    useToast(useT('workspace.taxSettings.saved'));
  }

  // Closed and reopened for another workspace while saving: leave that opening's form alone.
  if (!isSameOpening()) {
    return;
  }

  isSubmitting.value = false;

  if (response) {
    useDialogStore().close('workspaceTaxSettings');
  }
};
</script>
